import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Plus, X, Trash2 } from 'lucide-react';
import { useTemplates, useTemplateBatches, useDeleteTemplate } from '@/lib/hooks/useCurriculum';
import { CreateCurriculumModal } from './CreateCurriculumModal';
import { cn } from '@/lib/cn';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';

const statusColors: Record<string, string> = {
  draft: 'bg-yellow-100 text-yellow-800',
  published: 'bg-green-100 text-green-800',
  archived: 'bg-gray-100 text-gray-600',
};

function BatchUsageModal({ templateId, templateTitle, onClose }: {
  templateId: string;
  templateTitle: string;
  onClose: () => void;
}) {
  const { data: batches, isLoading } = useTemplateBatches(templateId);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-sm rounded-xl border border-border bg-card p-6 shadow-lg">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-base font-semibold">Batches using &ldquo;{templateTitle}&rdquo;</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="h-5 w-5" />
          </button>
        </div>
        {isLoading ? (
          <div className="flex h-16 items-center justify-center">
            <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          </div>
        ) : batches && batches.length > 0 ? (
          <ul className="divide-y divide-border">
            {batches.map((b) => (
              <li key={b.id} className="py-2">
                <p className="text-sm font-medium">{b.name}</p>
                <p className="text-xs text-muted-foreground">{b.org_name}</p>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground">No batches are using this template yet.</p>
        )}
      </div>
    </div>
  );
}

export function CurriculumListPage() {
  const navigate = useNavigate();
  const { data: templates, isLoading } = useTemplates();
  const [showCreate, setShowCreate] = useState(false);
  const [batchModal, setBatchModal] = useState<{ id: string; title: string } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<{ id: string; title: string } | null>(null);
  const deleteMutation = useDeleteTemplate();

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl p-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Curriculum</h1>
          <p className="text-sm text-muted-foreground">
            Create and manage curriculums, then fork them for specific batches.
          </p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          <Plus className="h-4 w-4" /> New Curriculum
        </button>
      </div>

      <div className="rounded-lg border border-border">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/50 text-left">
              <th className="px-4 py-3 font-medium">Curriculum</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium text-center">Modules</th>
              <th className="px-4 py-3 font-medium text-center">Topics</th>
              <th className="px-4 py-3 font-medium text-center">Hours</th>
              <th className="px-4 py-3 font-medium text-center">Batches</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {templates && templates.length > 0 ? (
              templates.map((t) => (
                <tr key={t.id} className="border-b border-border last:border-0 hover:bg-muted/30">
                  <td className="px-4 py-3">
                    <button
                      onClick={() => navigate({ to: '/admin/curriculum/$templateId', params: { templateId: t.id } })}
                      className="text-left"
                    >
                      <p className="font-medium text-foreground hover:text-primary">{t.title}</p>
                      <p className="font-mono text-xs text-muted-foreground">{t.slug}</p>
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <span className={cn('rounded-full px-2 py-0.5 text-xs font-medium', statusColors[t.status])}>
                      {t.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">{t.module_count}</td>
                  <td className="px-4 py-3 text-center">{t.topic_count}</td>
                  <td className="px-4 py-3 text-center">{t.total_hours.toFixed(0)}h</td>
                  <td className="px-4 py-3 text-center">
                    {t.usage_count > 0 ? (
                      <button
                        onClick={() => setBatchModal({ id: t.id, title: t.title })}
                        className="rounded px-1.5 py-0.5 text-primary underline-offset-2 hover:underline"
                        title="View batches using this template"
                      >
                        {t.usage_count}
                      </button>
                    ) : (
                      <span className="text-muted-foreground">0</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="inline-flex">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setConfirmDelete({ id: t.id, title: t.title })}
                              disabled={t.usage_count > 0}
                              className="text-muted-foreground hover:text-destructive"
                            >
                              <Trash2 />
                            </Button>
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>
                          {t.usage_count > 0
                            ? `Cannot delete — in use by ${t.usage_count} batch${t.usage_count === 1 ? '' : 'es'}`
                            : 'Delete curriculum'}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={6} className="px-4 py-12 text-center text-muted-foreground">
                  No curriculums yet. Create one to get started.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <CreateCurriculumModal open={showCreate} onClose={() => setShowCreate(false)} />

      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-sm rounded-xl border border-border bg-card p-6 shadow-lg">
            <h2 className="mb-2 text-base font-semibold">Delete curriculum?</h2>
            <p className="mb-6 text-sm text-muted-foreground">
              <span className="font-medium text-foreground">&ldquo;{confirmDelete.title}&rdquo;</span> and all its
              modules and topics will be permanently deleted. This cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setConfirmDelete(null)}
                className="rounded-lg border border-border px-4 py-2 text-sm font-medium hover:bg-muted"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  deleteMutation.mutate(confirmDelete.id, { onSuccess: () => setConfirmDelete(null) });
                }}
                disabled={deleteMutation.isPending}
                className="rounded-lg bg-destructive px-4 py-2 text-sm font-medium text-destructive-foreground hover:bg-destructive/90 disabled:opacity-60"
              >
                {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {batchModal && (
        <BatchUsageModal
          templateId={batchModal.id}
          templateTitle={batchModal.title}
          onClose={() => setBatchModal(null)}
        />
      )}
    </div>
  );
}
